import {
  createConsumerSchema,
  updateConsumerSchema,
  type Consumer,
  type ConsumerCreated,
  type CreateConsumerInput,
  type UpdateConsumerInput,
} from '@easyhelp/shared';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  useConsumers,
  useCreateConsumer,
  useProducts,
  useRevokeConsumer,
  useUpdateConsumer,
} from '../../../api/consumers';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ConsumerCreateDialog } from './ConsumerCreateDialog';
import { ConsumerEditDialog } from './ConsumerEditDialog';
import { ConsumerEventsDialog } from './ConsumerEventsDialog';
import { ConsumersTable } from './ConsumersTable';
import { ConsumerWebhookDialog } from './ConsumerWebhookDialog';
import { CredentialsDialog } from './CredentialsDialog';

type DialogState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'credentials'; created: ConsumerCreated }
  | { kind: 'edit'; consumer: Consumer }
  | { kind: 'revoke'; consumer: Consumer }
  | { kind: 'history'; consumer: Consumer }
  | { kind: 'webhook'; consumer: Consumer };

/**
 * Impostazioni › Consumer (PRD-003 US-1, PAGE-006): il registro delle installazioni autorizzate
 * ad aprire ticket, con credenziali OAuth2 client credentials (DEC-006). I prodotti, con prefissi e
 * configurazione, hanno la loro sezione (PAGE-007). Riservato all'amministratore (`RequireRole`).
 */
export function ConsumersPage() {
  const registry = useConsumers();
  const productList = useProducts();
  const createConsumer = useCreateConsumer();
  const updateConsumer = useUpdateConsumer();
  const revokeConsumer = useRevokeConsumer();
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [notice, setNotice] = useState<string | null>(null);

  const close = () => {
    setDialog({ kind: 'closed' });
    createConsumer.reset();
    updateConsumer.reset();
    revokeConsumer.reset();
  };

  const submitCreate = (input: CreateConsumerInput) => {
    createConsumer.mutate(createConsumerSchema.parse(input), {
      onSuccess: (created) => {
        createConsumer.reset();
        setDialog({ kind: 'credentials', created });
      },
    });
  };

  const submitEdit = (input: UpdateConsumerInput) => {
    if (dialog.kind !== 'edit') return;
    updateConsumer.mutate(
      { id: dialog.consumer.id, changes: updateConsumerSchema.parse(input) },
      {
        onSuccess: (consumer) => {
          close();
          setNotice(`${consumer.name} aggiornato: vale dai prossimi token.`);
        },
      }
    );
  };

  const confirmRevoke = () => {
    if (dialog.kind !== 'revoke') return;
    revokeConsumer.mutate(dialog.consumer.id, {
      onSuccess: (consumer) => {
        close();
        setNotice(`${consumer.name} revocato: nessun nuovo token e quelli già emessi non sono più accettati.`);
      },
    });
  };

  const isPending = registry.isPending || productList.isPending;
  const failed = registry.isError ? registry : productList.isError ? productList : null;

  return (
    <Stack spacing={4}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-start' }}
      >
        <Stack spacing={1}>
          <Typography variant="h5" component="h2">
            Consumer
          </Typography>
          <Typography color="text.secondary">
            Le installazioni dei prodotti autorizzate ad aprire ticket: ognuna ha prodotto, ambiente, committenti e una
            credenziale con cui ottiene i token.
          </Typography>
        </Stack>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setDialog({ kind: 'create' })}
          disabled={!registry.data || !productList.data}
          sx={{ flexShrink: 0 }}
        >
          Registra un consumer
        </Button>
      </Stack>

      {isPending && !failed && (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography>Caricamento del registro…</Typography>
        </Stack>
      )}

      {failed && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void failed.refetch()}>
              Riprova
            </Button>
          }
        >
          <AlertTitle>Registro non disponibile</AlertTitle>
          {failed.error.message}
        </Alert>
      )}

      {registry.data && (
        <Paper component="section" variant="outlined" aria-labelledby="consumers-title" sx={{ p: 4 }}>
          <Typography id="consumers-title" variant="h6" component="h3" sx={{ mb: 3 }}>
            Registro
          </Typography>
          {registry.data.consumers.length === 0 ? (
            <Typography color="text.secondary">
              Nessun consumer registrato: finché non ce n'è uno, nessuna installazione può aprire ticket.
            </Typography>
          ) : (
            <ConsumersTable
              consumers={registry.data.consumers}
              onEdit={(consumer) => setDialog({ kind: 'edit', consumer })}
              onRevoke={(consumer) => setDialog({ kind: 'revoke', consumer })}
              onHistory={(consumer) => setDialog({ kind: 'history', consumer })}
              onWebhook={(consumer) => setDialog({ kind: 'webhook', consumer })}
            />
          )}
        </Paper>
      )}

      {dialog.kind === 'create' && registry.data && productList.data && (
        <ConsumerCreateDialog
          open
          instanceEnvironment={registry.data.instanceEnvironment}
          products={productList.data.products}
          error={createConsumer.error?.message}
          busy={createConsumer.isPending}
          onSubmit={submitCreate}
          onClose={close}
        />
      )}

      {dialog.kind === 'credentials' && <CredentialsDialog open created={dialog.created} onClose={close} />}

      {dialog.kind === 'edit' && (
        <ConsumerEditDialog
          open
          key={dialog.consumer.id}
          consumer={dialog.consumer}
          error={updateConsumer.error?.message}
          busy={updateConsumer.isPending}
          onSubmit={submitEdit}
          onClose={close}
        />
      )}

      {dialog.kind === 'revoke' && (
        <ConfirmDialog
          open
          title={`Revocare ${dialog.consumer.name}?`}
          confirmLabel="Revoca"
          confirmColor="error"
          error={revokeConsumer.error?.message}
          blocked={revokeConsumer.error?.code === 'consumers.already_revoked'}
          busy={revokeConsumer.isPending}
          onConfirm={confirmRevoke}
          onClose={close}
        >
          L'installazione non otterrà più token e non potrà aprire ticket; i token già emessi non sono più accettati. La
          revoca è definitiva: per riattivarla si registra un nuovo consumer con nuove credenziali.
        </ConfirmDialog>
      )}

      {dialog.kind === 'history' && <ConsumerEventsDialog open consumer={dialog.consumer} onClose={close} />}

      {dialog.kind === 'webhook' && <ConsumerWebhookDialog open consumer={dialog.consumer} onClose={close} />}

      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)} message={notice} />
    </Stack>
  );
}
